/**
 * Phase 25.5 — Onboarding tour overlay (Shepherd.js).
 * Phase 25.5.1 hotfix — tour polish:
 *   - Step counter embedded in each step's HTML body (was a DOM-attr race).
 *   - Action-required steps show an explicit "I'll do it" / "Got it" button
 *     and the next step uses a longer beforeShowPromise timeout.
 *   - Modal-aware beforeShowPromise: pauses advancement while the
 *     VehicleEditor modal is open.
 *   - Cancel handler force-removes Shepherd DOM artifacts and closes any
 *     open modal so the X always exits cleanly.
 *   - Seeded scenario navigation: each tour opens a real demo scenario
 *     so result-dependent steps have data to highlight.
 *
 * Two mode-gated tours (Personal: 10 steps, Fleet: 12 steps) run fully under
 * user control: Next, Back, and a Skip cancel icon are available at every
 * step. The wrong-mode tour never renders.
 *
 * Auto-start fires once per mode on first entry, gated by:
 *   - `shouldAutoStartTour()` from useAppModeStore (checks tour-seen flags)
 *   - Route allow-list (suppressed on /methodology and /auth/*)
 *   - A 1500ms delay after activeMode resolves, giving the page time to
 *     render before Shepherd attaches.
 *
 * Mounted once in Layout — present on every authenticated page.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useAppModeStore, type AppMode } from '../../store/useAppModeStore';
import { useAuthStore } from '../../store/useAuthStore';

const AUTO_START_DELAY_MS = 1500;
const ELEMENT_WAIT_MS = 3000;
const ACTION_WAIT_MS = 8000;
const POST_NAV_DELAY_MS = 1500;
// v1.42.5 Fix 2: pre-tour route the user to the scenarios list so step 1
// renders against the correct backdrop regardless of where startTour() fires.
const TOUR_START_ROUTE = '/scenarios';
const PRE_TOUR_NAV_DELAY_MS = 800;

// Seeded QA scenarios owned by user 316f4edc-c9ab-480f-b008-977d8f5415a2.
// Used by the open-demo tour step so result-dependent steps have real data.
const PERSONAL_DEMO_SCENARIO_ID = '100cf7c4-321e-4d79-b1ba-a6499d1e1ecc';
const FLEET_DEMO_SCENARIO_ID = '65ef453f-f1c7-40bc-a3f7-1e0779cb6cf9';

// Routes where the tour is suppressed even if shouldAutoStartTour() is true.
const SUPPRESSED_ROUTE_PREFIXES = ['/methodology', '/auth/'];

/**
 * Returns true while any modal (VehicleEditor or other role="dialog" element)
 * is mounted. Tour steps pause advancement while this is true so a step
 * doesn't render behind / over an open modal.
 */
function isModalOpen(): boolean {
  return (
    document.querySelector('[data-vehicle-editor-open]') !== null ||
    document.querySelector('[role="dialog"]') !== null
  );
}

/**
 * Poll the DOM until `selector` matches an element or `timeoutMs` elapses.
 * If a modal is open we wait until it closes before considering the target
 * present — otherwise the Shepherd overlay would render under the modal.
 */
function waitForElement(
  selector: string,
  timeoutMs = ELEMENT_WAIT_MS,
): Promise<Element | null> {
  return new Promise((resolve) => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (!isModalOpen()) {
        const el = document.querySelector(selector);
        if (el) {
          resolve(el);
          return;
        }
      }
      if (Date.now() > deadline) {
        resolve(null);
        return;
      }
      window.setTimeout(tick, 100);
    };
    tick();
  });
}

interface StepDef {
  id: string;
  /** CSS selector to attach the step to. Omit for centered/unattached step. */
  target?: string;
  title: string;
  text: string;
  /** Default true. Pass false for first/last steps to drop the Back button. */
  back?: boolean;
  next?: boolean;
  /** Last step shows "Done" and triggers tour completion. */
  done?: boolean;
  /** Wait for target to mount + any modal to close before showing. */
  waitForTarget?: boolean;
  /** Override default wait timeout (used for action-dependent steps). */
  waitForTargetMs?: number;
  /** Custom Next-button label, e.g. "I'll do it" or "Got it". */
  nextLabel?: string;
  /** If set, the Next button navigates instead of advancing in-place. The
   * next step still runs beforeShowPromise to wait for the new page. */
  navigateTo?: string;
}

const PERSONAL_STEPS: StepDef[] = [
  {
    id: 'mode-toggle',
    target: '[data-tour="mode-toggle"]',
    title: "You're in Personal mode",
    text:
      "VehicleTCO has two products: Personal and Fleet. You're in Personal mode — built for comparing the true 5-year cost of owning any two vehicles. Switch to Fleet anytime for fleet operator tools.",
    back: false,
  },
  {
    id: 'scenarios-list',
    target: '[data-tour="scenarios-list"]',
    title: 'Your saved comparisons',
    text:
      'Scenarios are saved vehicle comparisons. Each one stores two vehicles, your assumptions, and the full cost analysis. You can run as many as you like.',
  },
  {
    id: 'open-demo',
    target: '[data-tour="scenarios-list"]',
    title: "Let's use a real example",
    text:
      "We'll walk through the Camry vs CR-V demo so you can see real results. Click the button below and we'll open the scenario for you.",
    nextLabel: 'Open it for me',
    navigateTo: `/scenario/individual/${PERSONAL_DEMO_SCENARIO_ID}`,
  },
  {
    id: 'vehicle-selector',
    target: '[data-tour="vehicle-selector"]',
    title: 'Pick your vehicles',
    text:
      'Search for a make and model. MSRP auto-fills from our database. Add a second vehicle and you can compare. Any two vehicles — gas, hybrid, or EV.',
    waitForTarget: true,
    waitForTargetMs: ACTION_WAIT_MS,
  },
  {
    id: 'assumptions-tab',
    target: '[data-tour="assumptions-tab"]',
    title: 'Set your assumptions',
    text:
      'Fuel prices default to live EIA data. Set your expected investment return rate (used to compute opportunity cost), sales tax, and ownership horizon. The tool is only as accurate as your inputs.',
    waitForTarget: true,
  },
  {
    id: 'input-completeness',
    target: '[data-tour="input-completeness"]',
    title: 'Review before you compute',
    text:
      'This panel flags inputs that are using model estimates rather than your actual numbers — things like insurance, MPG, and residual value. Review and override anything you know better.',
    waitForTarget: true,
  },
  {
    id: 'compute-button',
    target: '[data-tour="compute-button"]',
    title: 'Run the analysis',
    text:
      "Click <b>Compute</b> to run the full 5-year TCO calculation, then click <b>Got it</b> below. The tour will continue once the results page renders.",
    waitForTarget: true,
    nextLabel: 'Got it',
  },
  {
    id: 'tco-summary',
    target: '[data-tour="tco-summary"]',
    title: 'Your total cost of ownership',
    text:
      'The TCO summary shows every cost line — depreciation, fuel, insurance, maintenance, registration, and residual value reclaimed. The year-by-year chart shows how costs evolve over your ownership horizon.',
    waitForTarget: true,
    waitForTargetMs: ACTION_WAIT_MS,
  },
  {
    id: 'dep-curve',
    target: '[data-tour="dep-curve"]',
    title: 'How your vehicle loses value',
    text:
      "This curve shows your vehicle's projected resale value month by month, based on Argonne ANL model data. The residual value at your horizon is credited back against your TCO.",
    waitForTarget: true,
  },
  {
    id: 'scenario-diff-tab',
    target: '[data-tour="scenario-diff-tab"]',
    title: 'Compare two scenarios',
    text:
      "Save a second scenario and use the Scenario Comparison tab to compare them side by side. You'll see the full cost delta, break-even year, and sensitivity analysis showing how the winner changes if fuel prices or mileage shifts.",
    done: true,
  },
];

const FLEET_STEPS: StepDef[] = [
  {
    id: 'mode-toggle',
    target: '[data-tour="mode-toggle"]',
    title: "You're in Fleet mode",
    text:
      "Fleet mode is built for fleet operators and business vehicle decisions. You'll model total cost of ownership across multiple vehicles, vocations, and fuel types — including federal tax benefits.",
    back: false,
  },
  {
    id: 'scenarios-list',
    target: '[data-tour="scenarios-list"]',
    title: 'Your fleet configurations',
    text:
      'Each fleet scenario is a named configuration: a set of vehicles with quantities, vocations, and operating parameters. Save multiple scenarios to compare fleet options.',
  },
  {
    id: 'open-demo',
    target: '[data-tour="scenarios-list"]',
    title: "Let's use a real example",
    text:
      "We'll walk through the Isuzu NPR-HD demo fleet so you can see real results. Click the button below and we'll open it for you.",
    nextLabel: 'Open it for me',
    navigateTo: `/scenario/fleet/${FLEET_DEMO_SCENARIO_ID}`,
  },
  {
    id: 'fleet-line-items',
    target: '[data-tour="fleet-line-items"]',
    title: 'Build your fleet',
    text:
      'Each line item is a vehicle type with a quantity. Heterogeneous fleets — mixing gas, diesel, and EV vehicles — are fully supported. Each line can have its own vocation and fuel override.',
    waitForTarget: true,
    waitForTargetMs: ACTION_WAIT_MS,
  },
  {
    id: 'add-line-item',
    target: '[data-tour="add-line-item"]',
    title: "Open a vehicle to see its settings",
    text:
      "Click <b>Edit Vehicle</b> on any line item to open the vehicle editor — we'll walk through the vocation and tax settings inside. Then click <b>I'll do it</b> to continue.",
    waitForTarget: true,
    nextLabel: "I'll do it",
  },
  {
    id: 'vocation-selector',
    target: '[data-tour="vocation-selector"]',
    title: 'Set the duty cycle',
    text:
      'Vocation tells the tool how this vehicle is actually used — local delivery, long haul, refuse, transit, and more. It applies an NREL Fleet DNA derate to the manufacturer MPG, giving you a real-world fuel estimate instead of a lab rating.',
    waitForTarget: true,
    waitForTargetMs: ACTION_WAIT_MS,
  },
  {
    id: 'tax-settings',
    target: '[data-tour="tax-settings"]',
    title: 'Section 179 + bonus depreciation',
    text:
      'Business vehicles may qualify for immediate expensing under IRC §179 and bonus depreciation under §168(k). Set your marginal tax rate and business use percentage. The tool computes your first-year tax benefit and credits it against TCO. Consult a tax professional for your specific situation.',
    waitForTarget: true,
  },
  {
    id: 'fleet-health-check',
    target: '[data-tour="fleet-health-check"]',
    title: 'Confirm your fleet before computing',
    text:
      'Close the vehicle editor if it is still open, then this summary shows vehicle count, vocation mix, fuel type mix, and total acquisition cost. Review it before running the analysis.',
    waitForTarget: true,
    waitForTargetMs: ACTION_WAIT_MS,
  },
  {
    id: 'fleet-compute',
    target: '[data-tour="fleet-compute"]',
    title: 'Run the fleet analysis',
    text:
      "Click <b>Compute Fleet TCO</b> to run the full analysis across all vehicles, then click <b>Got it</b> below. The tour will continue once results appear.",
    waitForTarget: true,
    nextLabel: 'Got it',
  },
  {
    id: 'cpm-hero',
    target: '[data-tour="cpm-hero"]',
    title: 'Cost per mile — your primary metric',
    text:
      'For fleet operators, cost per mile is the decision metric. It normalizes across different vehicle types and quantities, letting you compare configurations on a per-mile basis regardless of fleet size.',
    waitForTarget: true,
    waitForTargetMs: ACTION_WAIT_MS,
  },
  {
    id: 'year-by-year-chart',
    target: '[data-tour="year-by-year-chart"]',
    title: 'How costs evolve over time',
    text:
      'The year-by-year chart shows annual total cost per year. Depreciation is front-loaded (highest in year 1) following the Argonne curve. Maintenance rises year-over-year using fleet industry benchmarks; fuel and insurance include annual inflation factors.',
    waitForTarget: true,
  },
  {
    id: 'scenario-diff-tab',
    target: '[data-tour="scenario-diff-tab"]',
    title: 'Compare fleet configurations',
    text:
      'Save a second fleet configuration and use Scenario Comparison to compare them side by side — full cost ledger, year-by-year grouped chart, break-even year, and sensitivity analysis showing how the winner changes if fuel prices or mileage shifts.',
    done: true,
  },
];

function buildTour(
  mode: AppMode,
  navigate: NavigateFunction,
  onComplete: () => void,
  onCancel: () => void,
): InstanceType<typeof Shepherd.Tour> {
  const steps = mode === 'fleet' ? FLEET_STEPS : PERSONAL_STEPS;

  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
      classes: 'vehicletco-tour',
    },
  });

  steps.forEach((step, idx) => {
    const isFirst = idx === 0;
    const isLast = !!step.done;
    const stepNumber = idx + 1;
    const total = steps.length;

    const buttons: {
      text: string;
      action?: () => unknown;
      classes?: string;
      secondary?: boolean;
    }[] = [];

    if (!isFirst && step.back !== false) {
      buttons.push({
        text: 'Back',
        action: () => tour.back(),
        classes: 'shepherd-button-secondary',
        secondary: true,
      });
    }
    if (isLast) {
      buttons.push({
        text: 'Done',
        action: () => tour.complete(),
        classes: 'shepherd-button-primary',
      });
    } else if (step.next !== false) {
      const primaryLabel = step.nextLabel ?? 'Next';
      buttons.push({
        text: primaryLabel,
        action: () => {
          if (step.navigateTo) {
            navigate(step.navigateTo);
            // Give the new route time to mount before the next step's
            // beforeShowPromise begins polling for its target.
            window.setTimeout(() => tour.next(), POST_NAV_DELAY_MS);
          } else {
            tour.next();
          }
        },
        classes: 'shepherd-button-primary',
      });
    }

    // Step counter embedded in the step body so it survives Shepherd's
    // per-step DOM recycle (Phase 25.5.1 — replaces the attribute-based
    // approach which lost the counter mid-tour).
    const counterHtml =
      `<span class="tour-step-counter">Step ${stepNumber} of ${total}</span>`;
    const bodyHtml = `${step.text}${counterHtml}`;

    const attach = step.target ? { element: step.target, on: 'bottom' as const } : undefined;

    tour.addStep({
      id: step.id,
      title: step.title,
      text: bodyHtml,
      attachTo: attach,
      buttons,
      beforeShowPromise: step.waitForTarget && step.target
        ? async () => {
            const el = await waitForElement(
              step.target!,
              step.waitForTargetMs ?? ELEMENT_WAIT_MS,
            );
            if (!el) {
              // Element never appeared — skip this step rather than letting
              // Shepherd attach to <body> and break the visual flow.
              setTimeout(() => {
                if (isLast) tour.complete();
                else tour.next();
              }, 0);
            }
          }
        : undefined,
    });
  });

  tour.on('complete', onComplete);
  tour.on('cancel', onCancel);

  return tour;
}

// Process-local: holds the live tour so the help-icon dropdown and route
// changes can cancel it. Window-level so an external caller (the ? menu)
// can request a fresh tour without prop-drilling a ref.
declare global {
  interface Window {
    __vehicletcoTour?: {
      start: (mode: AppMode) => void;
      cancel: () => void;
    };
  }
}

/**
 * Force-remove any lingering Shepherd DOM artifacts. Called from the cancel
 * handler so the X button is guaranteed to clear the overlay even if a
 * modal/race left a node behind (Phase 25.5.1).
 */
function purgeShepherdDom() {
  document
    .querySelectorAll(
      '.shepherd-modal-overlay-container, .shepherd-element, .shepherd-arrow',
    )
    .forEach((el) => el.remove());
  document.body.classList.remove('shepherd-active');
}

export default function VehicleTCOTour() {
  const activeMode = useAppModeStore((s) => s.activeMode);
  const initialized = useAppModeStore((s) => s.initialized);
  const tourActive = useAppModeStore((s) => s.tourActive);
  const setTourActive = useAppModeStore((s) => s.setTourActive);
  const shouldAutoStartTour = useAppModeStore((s) => s.shouldAutoStartTour);
  const markTourSeen = useAppModeStore((s) => s.markTourSeen);
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tourRef = useRef<InstanceType<typeof Shepherd.Tour> | null>(null);

  const isSuppressedRoute = SUPPRESSED_ROUTE_PREFIXES.some((p) =>
    pathname.startsWith(p),
  );

  // Stable function refs in closures pick up the latest mode / auth via this
  // ref so we don't have to tear down and rebuild the tour on each render.
  const initAndStartTour = (mode: AppMode) => {
    if (tourRef.current) {
      tourRef.current.cancel();
      tourRef.current = null;
    }
    setTourActive(true);
    const tour = buildTour(
      mode,
      navigate,
      // onComplete — explicit Done
      () => {
        setTourActive(false);
        tourRef.current = null;
        purgeShepherdDom();
        void markTourSeen(mode, true, !!user);
      },
      // onCancel — Skip / cancel icon / Escape
      () => {
        setTourActive(false);
        tourRef.current = null;
        purgeShepherdDom();
        void markTourSeen(mode, true, !!user);
      },
    );
    tourRef.current = tour;
    tour.start();
  };

  // v1.42.5 Fix 2: if the user triggers the tour from a scenario output page,
  // navigate to /scenarios first so the modal backdrop hugs the right context.
  const startTour = (mode: AppMode) => {
    if (window.location.pathname !== TOUR_START_ROUTE) {
      navigate(TOUR_START_ROUTE);
      window.setTimeout(() => initAndStartTour(mode), PRE_TOUR_NAV_DELAY_MS);
    } else {
      initAndStartTour(mode);
    }
  };

  // Expose start/cancel hooks for the ? help menu and any future caller.
  useEffect(() => {
    window.__vehicletcoTour = {
      start: (mode: AppMode) => startTour(mode),
      cancel: () => {
        if (tourRef.current) {
          tourRef.current.cancel();
          tourRef.current = null;
        }
      },
    };
    return () => {
      delete window.__vehicletcoTour;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Auto-start: fires once per (mode, profile-load) when the user hasn't
  // seen the active mode's tour yet.
  useEffect(() => {
    if (!initialized) return;
    if (isSuppressedRoute) return;
    if (tourActive) return;
    if (!shouldAutoStartTour()) return;
    const timer = window.setTimeout(() => {
      // Re-check inside the timer: the user may have switched modes or
      // navigated to a suppressed route during the delay.
      if (!shouldAutoStartTour()) return;
      startTour(activeMode);
    }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, activeMode, isSuppressedRoute]);

  // Cancel the tour if we land on a suppressed route mid-tour (rare; safety
  // net so a stale modal overlay doesn't trap the user on /methodology).
  useEffect(() => {
    if (isSuppressedRoute && tourRef.current) {
      tourRef.current.cancel();
      tourRef.current = null;
    }
  }, [isSuppressedRoute]);

  // Escape key closes the tour cleanly. Shepherd's cancelIcon handles the X
  // click; this is a safety net for keyboard users.
  useEffect(() => {
    if (!tourActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && tourRef.current) {
        tourRef.current.cancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tourActive]);

  return null;
}
